'use client'

// components/LeadForms/CorporateLeadForm.tsx
import { Spinner } from "@components/atoms";
import PhoneInput from "@components/atoms/PhoneInput";
import { generateId } from "../LeadForm/helper";
import { Anchor, Button, Group, PinInput, Text } from "@mantine/core";
import React from 'react';

export interface CorporateLeadFormProps {
    /** A unique name for the form to prefix element IDs. */
    formName: string;
    isds: Record<string, string>[];
    getQuoteForm: any;
    otp: string;
    time: number;
    showOTP: boolean;
    errors: {
        phone?: string;
        name?: string;
        otp?: string;
    };
    onMobileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onMobileReset: () => void;
    handleOTP?: (val: string) => void;
    onGetOTP: () => void;
    onViewPlan: () => void;
    initData: any;
    isLoading: boolean;
    onCountryChange: (val: string) => void;
}

const maskPhone = (phone: string) => phone.slice(0, 5) + "••••";

const CorporateLeadForm: React.FC<CorporateLeadFormProps> = ({
    formName,
    getQuoteForm,
    isds,
    otp,
    time,
    showOTP,
    errors,
    onMobileChange,
    handleOTP,
    onMobileReset,
    onCountryChange,
    initData,
    onGetOTP,
    onViewPlan,
    isLoading
}) => {
    const phone = getQuoteForm?.phoneNumber;
    const defaultCountry = "India";
    const hasFieldErrors = !getQuoteForm?.phoneNumber || !!errors.phone || !!errors.otp;

    const isOtpInvalid = showOTP && (otp.length !== 4 || !!errors.otp);

    const isFormInvalid = hasFieldErrors || isOtpInvalid;

    return (
        <section className="bg-white">
            <div className="flex flex-col gap-2">
                {/* Mobile Number */}
                <PhoneInput
                    id={generateId("mobile-number-input", formName)}
                    isds={isds}
                    inputValue={getQuoteForm?.phoneNumber}
                    countryValue={getQuoteForm?.country || defaultCountry}
                    onMobileChange={onMobileChange}
                    onCountryChange={onCountryChange}
                    isMask={initData?.channel == "Tele Caller" || false}
                    onMobileReset={onMobileReset}
                    newDesignConfig={false}
                    isLeadForm
                    corporateLeadForm
                />
                {errors.phone && (
                    <span className="text-xs text-red-700 pl-2">
                        {errors.phone}
                    </span>
                )}

                {/* OTP Section */}
                {showOTP && (
                    <>
                        <Text
                            id={generateId("otp-label", formName)}
                            htmlFor={generateId("otp-input", formName)}
                            component="label"
                            fw={600}
                            size="sm"
                            className="mb-2"
                        >
                            Enter the verification code
                        </Text>
                        <Text size="xs" color="dimmed">
                            [OTP has been sent to {maskPhone(phone)}]
                        </Text>

                        <Group align="center" mb="sm">
                            <PinInput
                                id={generateId("otp-input", formName)}
                                length={4}
                                value={otp}
                                onChange={handleOTP}
                                size="lg"
                                type="number"
                            />
                        </Group>
                        {errors.otp && (
                            <span className="text-xs text-red-700">
                                {errors.otp}
                            </span>
                        )}
                        <Text size="xs" color="dimmed" mb="sm">
                            {time > 0 ? (
                                <>Resend the code in {time}s</>
                            ) : (
                                <Anchor
                                    id={generateId("resend-otp-button", formName)}
                                    component="button"
                                    size="xs"
                                    onClick={onGetOTP}
                                >
                                    Resend code
                                </Anchor>
                            )}
                        </Text>
                    </>
                )}

                {/* Disclaimer */}
                <div className="flex items-start gap-2 text-xxs opacity-60 my-2">
                    <img
                        alt="Disclaimer info"
                        loading="lazy"
                        src="/ethinos-home-page/svgs/disclaimer-info.svg"
                        height="15"
                        width="15"
                    />
                    <span>
                        By clicking {showOTP ? "Get Quote" : "Get OTP"}, you authorize Niva
                        Bupa to Call/SMS/Whatsapp on your registered mobile overriding DNCR.
                    </span>
                </div>

                {/* Action Button */}
                <Button
                    id={generateId(showOTP ? "get-quote-button" : "get-otp-button", formName)}
                    fullWidth
                    disabled={isFormInvalid || isLoading}
                    onClick={showOTP ? onViewPlan : onGetOTP}
                    className={`hover:opacity-90 transition-opacity ${isFormInvalid || isLoading
                        ? "bg-orange-300 text-white cursor-not-allowed"
                        : "bg-nivabupa-ethinosHomeOrange text-white rounded-md w-full px-10 py-3 hover:opacity-90 transition-opacity"}`}
                >
                    {isLoading ? <Spinner /> : showOTP ? "Get Quote" : "Get OTP"}
                </Button>
            </div>
        </section>
    );
};

export default CorporateLeadForm;